import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useApolloClient, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { decode } from 'he';
import {
  GET_TOPICS_PAGE,
  GET_MEDIA_ASSETS,
  GET_TINYCMS_FORMS,
  GET_TINYCMS_PAGES,
  GET_PUBLIC_SITE,
  GET_TINYCMS_DELETED_ARTICLES,
  RESTORE_TINYCMS_ARTICLE,
} from '../../graphql';
import { RecentlyDeleted, type DeletedItem } from '../cms/RecentlyDeleted';
import { CmsPagesView } from '../cms/CmsPages';
import { CmsFormsView } from '../cms/CmsForms';
import { CmsAuthorsView } from '../cms/CmsAuthors';
import { CmsAnalytics } from '../cms/CmsAnalytics';
import { GET_TINYCMS_AUTHORS } from '../cms/authors';
import { AgentFirstButton, openAskAgent } from '../cms/AskYourAgent';
import { NewPageDialog } from '../cms/NewPageDialog';
import { groupTopics, TopicFilter, type TopicGroup } from '../cms/TopicFilter';
import { SourceFilter, type SourceCount } from '../cms/SourceFilter';
import { FeedBringInBanner, type BringInShow } from '../cms/FeedBringIn';
import {
  TYPE_SETTINGS,
  isManageArea,
  ManageArea,
  ManageView,
  manageUrl,
  movedManageArea,
} from '../cms/ManageView';
import { primaryCategory } from '../cms/categories';
import { useAccount } from '../../context/AccountContext';
import { useAddons } from '../cms/addons';
import { SECTION_TITLES } from '../nav/planNav';
import { MediaLibrary } from './MediaLibrary';
import { plainText } from '../../lib/plainText';
import { ARTICLE_STATUS, articleBucket, emptyLive } from '../statusWords';
import { useSeesExtractedDetail } from '../settings/supportAccess';

type LibraryFilter = 'all' | 'review' | 'draft' | 'scheduled' | 'live';

/** Category chips shown before "more": the most used ones. */
type ContentView = 'pages' | 'articles' | 'authors' | 'forms' | 'media' | 'analytics' | 'manage';

interface ContentTopic {
  id: string;
  title?: string | null;
  summary?: string | null;
  publishedDate?: string | null;
  publishStatus?: string | null;
  /** It has no text (topicsPage sets it). */
  bodyEmpty?: boolean | null;
  contentType?: 'webpage' | 'video' | 'image' | 'article' | null;
  category?: string | null;
  atomizedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  verifiedFacts?: unknown[] | null;
  dataSource?: { id: string; name: string; type: string } | null;
  /** Other live articles with the same title and body (Prototype v14). */
  duplicateCount?: number | null;
  /** Waiting on a yes, by Today's rule (topicsPage sets it for its rows). */
  awaitingApproval?: boolean | null;
  /** The byline as names, author first; [] = no author (topicsPage sets it). */
  byline?: string[] | null;
  /** From a feed, not in TinyCMS yet: the "From feed" tag (topicsPage sets it). */
  fromFeed?: boolean | null;
  /** Listing a Bring all in's flagged articles: what checks found first. */
  bringInFinding?: string | null;
}

/** The Author column: "Dana Ruiz", "Dana Ruiz +1", or "No author". */
export function bylineLabel(names: string[]): string {
  if (!names.length) return 'No author';
  return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
}

/**
 * One status per record, so the chip counts add up to All (the server's
 * topicsPage buckets, in the same order). To approve is an article waiting
 * on a yes — the same rule as Today's "need you", so the two counts agree.
 * Whether Prism AI has read it is the Facts column, not a status.
 */
function libraryFilter(topic: ContentTopic): Exclude<LibraryFilter, 'all'> {
  return articleBucket(topic.awaitingApproval, topic.publishStatus);
}

function statusLabel(topic: ContentTopic) {
  return ARTICLE_STATUS[libraryFilter(topic)];
}

interface GetTopicsPageResp {
  topicsPage: {
    items: ContentTopic[];
    total: number;
    offset: number;
    limit: number;
    counts: Record<LibraryFilter, number>;
    categories: Array<{ name: string; count: number }>;
    sources?: SourceCount[];
  };
}

/** Articles per page of the Content list. */
const PAGE_SIZE = 50;

const STATUS_STYLE: Record<Exclude<LibraryFilter, 'all'>, { dot: string; text: string }> = {
  live: { dot: 'var(--ink-green)', text: 'var(--ink-ink)' },
  review: { dot: 'var(--ink-amber)', text: 'var(--ink-amber)' },
  draft: { dot: 'var(--ink-ink3)', text: 'var(--ink-ink2)' },
  scheduled: { dot: 'var(--ink-accent)', text: 'var(--ink-ink)' },
};

/** Short codes are stored lowercase. The library shows them as NCAAF, MLB. */
function topicLabel(value?: string | null) {
  const raw = primaryCategory(value);
  if (!raw) return '—';
  if (/^[a-z0-9]{1,8}$/i.test(raw)) return raw.toUpperCase();
  return raw;
}

function stamp(topic: ContentTopic) {
  return topic.updatedAt || topic.publishedDate || topic.createdAt || null;
}

function clock(iso: string) {
  const d = new Date(iso);
  return d.getMinutes() === 0 ? format(d, 'h a') : format(d, 'h:mm a');
}

function rowTime(topic: ContentTopic) {
  const iso = stamp(topic);
  if (!iso) return '—';
  const time = clock(iso);
  if (libraryFilter(topic) === 'scheduled') return `${format(new Date(iso), 'EEE')} ${time}`;
  return time;
}

function dayBucket(topic: ContentTopic) {
  if (libraryFilter(topic) === 'scheduled')
    return { key: 'scheduled', label: 'SCHEDULED', order: 0 };
  const iso = stamp(topic);
  if (!iso) return { key: 'earlier', label: 'EARLIER', order: 100000 };
  const d = new Date(iso);
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(new Date()) - start(d)) / 86400000);
  const dateLabel = format(d, 'MMM d').toUpperCase();
  if (diff === 0) return { key: 'today', label: `TODAY · ${dateLabel}`, order: 1 };
  if (diff === 1) return { key: 'yesterday', label: `YESTERDAY · ${dateLabel}`, order: 2 };
  return { key: `day-${start(d)}`, label: dateLabel, order: 3 + Math.max(diff, 0) };
}

/** Articles written in the same 10 minutes by the same source are one publish run. */
function batchKey(topic: ContentTopic) {
  const iso = topic.createdAt || topic.updatedAt || topic.publishedDate;
  if (!iso) return null;
  const bucket = Math.floor(new Date(iso).getTime() / 600000);
  return `${topic.dataSource?.id || 'manual'}|${bucket}`;
}

function markFor(name?: string | null) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'MC').slice(0, 2).toUpperCase();
}

function factLabel(topic: ContentTopic) {
  const n = topic.verifiedFacts?.length || 0;
  if (!n) return null;
  return n === 1 ? '1 fact' : `${n} facts`;
}

const FILTERS: Array<{ id: LibraryFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'review', label: ARTICLE_STATUS.review },
  { id: 'draft', label: 'Drafts' },
  { id: 'scheduled', label: ARTICLE_STATUS.scheduled },
  { id: 'live', label: ARTICLE_STATUS.live },
];

export function ContentLayout() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const viewParam = params.get('view');
  const [search, setSearch] = useState<string>('');
  // ?filter=scheduled opens on that chip (Today → Scheduled's "Calendar →").
  const [filter, setFilter] = useState<LibraryFilter>(
    () => FILTERS.find((f) => f.id === params.get('filter'))?.id || 'all'
  );
  // The Topic dropdown's choice: one topic, all its spellings (TopicFilter).
  const [topic, setTopic] = useState<TopicGroup | null>(null);
  // New page ▾ → Build it myself.
  const [newPageOpen, setNewPageOpen] = useState(false);
  const uploadMedia = useRef<(() => void) | null>(null);
  const [openBatches, setOpenBatches] = useState<Record<string, boolean>>({});
  // Which page of the list (the server filters, counts and pages; PAGE_SIZE at a time).
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState('');
  // Source: all | app (made in TinyCMS) | waiting (feed, not brought in) | a feed's id.
  const [source, setSource] = useState('all');
  // The feed banner's "Show the 312" / "Show the 16": one Bring all in's list.
  const [jobList, setJobList] = useState<Extract<BringInShow, { kind: 'job' }> | null>(null);

  const { accountId } = useAccount();
  // Facts come from the Prism AI add-on: they show while the site holds it
  // (even switched off), and only to TinyCMS technical support (supportAccess.tsx).
  // To approve is approvals, on every site.
  const { atomizeHeld: addonHeld, flatNav } = useAddons();
  const seesExtracted = useSeesExtractedDetail(accountId);
  const atomizeHeld = addonHeld && seesExtracted;

  const { data: mediaData } = useQuery(GET_MEDIA_ASSETS, {
    variables: { accountId, limit: 1, offset: 0 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  // The tab counts uploads, the same number as Media's All (App Prototype,
  // backlog ebda8050). Images linked from feeds have their own count in their
  // section, so "Media 2,414" next to "All 2" no longer happens.
  const mediaCount: number = mediaData?.mediaAssets?.total ?? 0;
  const mediaKnown = Boolean(mediaData);
  const { data: pagesData } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const pageCount: number = pagesData?.tinycmsPages?.length ?? 0;
  const { data: formsData } = useQuery(GET_TINYCMS_FORMS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const formRows: Array<{ responses?: { unread?: number } }> = formsData?.tinycmsForms || [];
  const { data: authorsData } = useQuery(GET_TINYCMS_AUTHORS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const authorCount: number = authorsData?.tinycmsAuthors?.length ?? 0;
  // An author's details open for editing (CmsAuthorsView tells us).
  const [authorEditing, setAuthorEditing] = useState(false);
  // One primary action at a time (UX review round 11): no authors yet, the
  // screen's own buttons lead; while the form is open, it does.
  const authorHeaderButtons = authorCount > 0 && params.get('author') !== 'new' && !authorEditing;
  const formCount = formRows.length;
  const formUnread = formRows.reduce((n, f) => n + (f.responses?.unread || 0), 0);

  // Public / Password / Offline, as Site settings sets it.
  const { data: publicData } = useQuery(GET_PUBLIC_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const publicAccount = publicData?.account;
  const visibility = !publicAccount
    ? null
    : !publicAccount.publicEnabled
      ? { label: 'Offline', dot: 'var(--ink-ink3)' }
      : publicAccount.publicAccessProtected
        ? { label: 'Password', dot: 'var(--ink-amber)' }
        : {
            label: 'Public',
            dot: 'var(--ink-green)',
            hiddenFromSearch: Boolean(publicAccount.publicSearchBlocked),
          };

  // Search waits for a pause in typing, then asks the server.
  useEffect(() => {
    const t = window.setTimeout(() => setQuery(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);
  // A new filter, category or search starts again at the first page.
  const categoryKey = topic?.key ?? '';
  const jobKey = jobList ? `${jobList.jobId}:${jobList.outcome}:${jobList.check || ''}` : '';
  useEffect(() => setOffset(0), [filter, categoryKey, query, source, jobKey]);

  const {
    data,
    previousData,
    loading,
    error: topicsError,
    refetch: refetchTopics,
  } = useQuery<GetTopicsPageResp>(GET_TOPICS_PAGE, {
    variables: {
      accountId,
      filter,
      categories: topic?.names ?? [],
      search: query || null,
      source,
      bringInJob: jobList?.jobId ?? null,
      bringInOutcome: jobList?.outcome ?? null,
      bringInCheck: jobList?.check ?? null,
      offset,
      limit: PAGE_SIZE,
    },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  // Keep the current page on screen while the next one loads — but not after
  // it fails: then the error shows, never the previous results as if current.
  const page = (data ?? (loading ? previousData : undefined))?.topicsPage;
  const { data: deletedData, refetch: refetchDeleted } = useQuery(GET_TINYCMS_DELETED_ARTICLES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const deletedArticles: DeletedItem[] = deletedData?.tinycmsDeletedArticles || [];
  const apollo = useApolloClient();

  const topics = useMemo(() => page?.items || [], [page]);
  const total = page?.total ?? 0;
  const sources = page?.sources ?? [];
  const hasFeeds = sources.some((s) => s.id !== null);
  // The banner's buttons narrow the list; the article screen's "Bring all in"
  // arrives with ?bring=all and opens the confirm.
  const showFromBanner = (show: BringInShow) => {
    if (show.kind === 'waiting') {
      setJobList(null);
      setSource('waiting');
    } else {
      setSource('all');
      setJobList(show);
    }
    setFilter('all');
  };
  const bringAsk = params.get('bring') === 'all';
  const clearBringAsk = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('bring');
        return next;
      },
      { replace: true }
    );
  }, [setParams]);
  const refetchList = useCallback(() => void refetchTopics(), [refetchTopics]);

  // A tab picked (?view=) wins. Otherwise Pages, unless there are articles;
  // until the articles have loaded, Articles shows its loading state. (A
  // failed load counts as none.)
  const view: ContentView =
    viewParam === 'media' ||
    viewParam === 'pages' ||
    viewParam === 'articles' ||
    viewParam === 'authors' ||
    viewParam === 'forms' ||
    viewParam === 'analytics' ||
    viewParam === 'manage'
      ? viewParam
      : viewParam === 'design'
        ? 'manage'
        : accountId && page && page.counts.all === 0
          ? 'pages'
          : 'articles';
  // In the flat nav the address names the section, so the right pill lights up
  // once a site with no articles has landed on Pages.
  useEffect(() => {
    if (!flatNav || viewParam || !accountId || !page) return;
    setParams(
      (prev) => {
        prev.set('view', view);
        return prev;
      },
      { replace: true }
    );
  }, [flatNav, viewParam, accountId, page, view, setParams]);
  // Content → Manage's area (?area=); the old ?view=design opens its Design area.
  const areaParam = params.get('area');
  const manageArea: ManageArea = isManageArea(areaParam)
    ? areaParam
    : viewParam === 'design'
      ? 'design'
      : 'site';
  // Whole-account counts per status, from the server (not just this page).
  // Blank until they load: a 0 while loading reads as "my site is empty".
  const counts = page?.counts ?? null;
  // The site has no articles yet (not a filter that matched none).
  const noArticles = counts?.all === 0 && !query && !topic && source === 'all' && !jobList;
  const shownCount = (n: number | undefined) => (counts && n != null ? n.toLocaleString() : '');
  // Pages, Forms, Media: "2,563" style, nothing when there is nothing.
  const tabCount = (n: number) => (n ? n.toLocaleString() : '');
  // A tab's count before it's known: a small grey bar, not a blank (QA round 7).
  const countSlot = (known: boolean, text: string) =>
    known ? (
      <span className="ink-seg-count">{text}</span>
    ) : (
      <span className="ink-seg-count" aria-hidden="true">
        <span className="ink-skel-bar ink-seg-count-skel" />
      </span>
    );
  // Every topic in the account, merged by case, most used first (so a feed that
  // tags each article many ways still leads with the topics that matter).
  const topicGroups = useMemo(
    () => groupTopics(page?.categories || [], (name) => topicLabel(name)),
    [page]
  );

  const q = query.toLowerCase();
  // Already filtered, searched and in order (newest activity first) by the server.
  const items = topics;

  const rows = useMemo(() => {
    const byDay = new Map<string, { label: string; order: number; items: ContentTopic[] }>();
    for (const topic of items) {
      const day = dayBucket(topic);
      const group = byDay.get(day.key) || { label: day.label, order: day.order, items: [] };
      group.items.push(topic);
      byDay.set(day.key, group);
    }
    const out: Array<
      | { kind: 'day'; key: string; label: string }
      | { kind: 'batch'; key: string; label: string; sub: string; time: string; mark: string }
      | { kind: 'more'; key: string; batch: string; label: string }
      | { kind: 'article'; topic: ContentTopic; indent: boolean }
    > = [];
    const days = [...byDay.entries()].sort((a, b) => a[1].order - b[1].order);
    for (const [dayKey, day] of days) {
      out.push({ kind: 'day', key: dayKey, label: day.label });
      const seen = new Set<string>();
      for (const topic of day.items) {
        if (seen.has(topic.id)) continue;
        const key = batchKey(topic);
        const mates = key ? day.items.filter((t) => batchKey(t) === key) : [topic];
        if (key && mates.length >= 4) {
          mates.forEach((t) => seen.add(t.id));
          const open = Boolean(openBatches[key]) || Boolean(q);
          const source = topic.dataSource?.name || 'an agent';
          const cats = [...new Set(mates.map((t) => primaryCategory(t.category)).filter(Boolean))];
          out.push({
            kind: 'batch',
            key,
            label: `Batch of ${mates.length} published by ${source}`,
            sub: cats.length ? `· ${topicLabel(cats[0])}` : '',
            time: rowTime(topic),
            mark: markFor(source),
          });
          for (const mate of open ? mates : mates.slice(0, 3)) {
            out.push({ kind: 'article', topic: mate, indent: true });
          }
          if (!open && mates.length > 3) {
            out.push({
              kind: 'more',
              key: `${key}-more`,
              batch: key,
              label: `Show ${mates.length - 3} more from this batch`,
            });
          } else if (open && mates.length > 3 && !q) {
            out.push({ kind: 'more', key: `${key}-more`, batch: key, label: 'Collapse batch' });
          }
        } else {
          seen.add(topic.id);
          out.push({ kind: 'article', topic, indent: false });
        }
      }
    }
    return out;
  }, [items, openBatches, q]);

  const toggleBatch = (key: string) => {
    setOpenBatches((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Always explicit, so choosing Articles sticks even when there are none yet.
  const setView = (next: ContentView) => setParams({ view: next });

  return (
    // The whole screen scrolls with the page, under the frosted top bar.
    <div className="ink-page ink-page-flow flex w-full flex-col">
      <div className="flex flex-col flex-1">
        <div className="w-full max-w-full overflow-x-clip relative flex-1 flex flex-col">
          <div className="ink-canvas m-0 w-full relative box-border flex flex-col flex-1">
            {/* The title row, then the view, rise in (ink.css [data-enter-blocks]). */}
            <div className="ink-frame" data-enter-blocks="">
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                {/* Without tabs the title names the view, which the top bar's tab may not. */}
                <h1 className={flatNav ? 'ink-h1' : 'ink-h1 ink-tabtitle'}>
                  {flatNav ? SECTION_TITLES[view] : 'Website'}
                </h1>
                {flatNav ? null : (
                  <div className="ink-seg ink-seg-counts">
                    <button
                      type="button"
                      data-active={view === 'pages' ? 'true' : 'false'}
                      onClick={() => setView('pages')}
                    >
                      Pages
                      {countSlot(Boolean(pagesData), tabCount(pageCount))}
                    </button>
                    <button
                      type="button"
                      data-active={view === 'articles' ? 'true' : 'false'}
                      onClick={() => setView('articles')}
                    >
                      Articles
                      {countSlot(Boolean(counts), shownCount(counts?.all))}
                    </button>
                    <button
                      type="button"
                      data-active={view === 'media' ? 'true' : 'false'}
                      onClick={() => setView('media')}
                    >
                      Media
                      {countSlot(mediaKnown, tabCount(mediaCount))}
                    </button>
                    <button
                      type="button"
                      data-active={view === 'authors' ? 'true' : 'false'}
                      onClick={() => setView('authors')}
                    >
                      Authors
                      {countSlot(Boolean(authorsData), tabCount(authorCount))}
                    </button>
                    <button
                      type="button"
                      data-active={view === 'forms' ? 'true' : 'false'}
                      onClick={() => setView('forms')}
                    >
                      Forms
                      {formUnread ? (
                        <span className="fm-tab-new" title={`${formUnread} new responses`}>
                          {formUnread} new
                        </span>
                      ) : (
                        countSlot(Boolean(formsData), tabCount(formCount))
                      )}
                    </button>
                    <button
                      type="button"
                      data-active={view === 'analytics' ? 'true' : 'false'}
                      onClick={() => setView('analytics')}
                    >
                      Analytics
                    </button>
                    <button
                      type="button"
                      data-active={view === 'manage' ? 'true' : 'false'}
                      onClick={() => setView('manage')}
                    >
                      Manage
                    </button>
                  </div>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {visibility ? (
                    <Link
                      to={manageUrl('site', 'visibility')}
                      className="ink-vis"
                      title="Who can see the site"
                    >
                      <span className="ink-dot" style={{ background: visibility.dot }} />
                      {visibility.label}
                    </Link>
                  ) : null}
                  {/* Site-wide settings live in Manage; a tab's own are named for what they
                      open (Round 44 §1), set apart from the site's status by a divider. */}
                  {TYPE_SETTINGS[view] ? (
                    <>
                      {visibility ? <span className="ink-head-divider" aria-hidden="true" /> : null}
                      <Link
                        to={TYPE_SETTINGS[view].to}
                        className="ink-btn"
                        title={TYPE_SETTINGS[view].tip}
                      >
                        {TYPE_SETTINGS[view].label}
                      </Link>
                    </>
                  ) : null}
                  {/* Agent-first New (designer's notes, 2026-10-25): Ask your agent first,
                      then the person's own way; New form has no form of its own to fill in. */}
                  {view === 'authors' ? (
                    authorHeaderButtons ? (
                      <AgentFirstButton
                        action="author"
                        label="New author"
                        disabled={!accountId}
                        onSelf={() => setParams({ view: 'authors', author: 'new' })}
                      />
                    ) : null
                  ) : view === 'pages' ? (
                    <AgentFirstButton
                      action="page"
                      label="New page"
                      disabled={!accountId}
                      onSelf={() => setNewPageOpen(true)}
                    />
                  ) : view === 'forms' ? (
                    <AgentFirstButton action="form" label="New form" disabled={!accountId} />
                  ) : view === 'articles' ? (
                    <AgentFirstButton
                      action="article"
                      label="New article"
                      disabled={!accountId}
                      onSelf={() => navigate('/content/new')}
                    />
                  ) : view === 'manage' || view === 'analytics' ? null : (
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={!accountId}
                      onClick={() => uploadMedia.current?.()}
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {newPageOpen ? (
                <NewPageDialog
                  onClose={() => setNewPageOpen(false)}
                  onCreated={(id) => {
                    setNewPageOpen(false);
                    setParams({ view: 'pages', page: id });
                  }}
                  onAskAgent={() => {
                    setNewPageOpen(false);
                    openAskAgent({ action: 'page', onSelf: () => setNewPageOpen(true) });
                  }}
                />
              ) : null}
              {view === 'pages' ? (
                <div className="flex-1">
                  <CmsPagesView
                    initial={params.get('page')}
                    fix={params.get('fix') === '1'}
                    block={params.get('block')}
                  />
                </div>
              ) : view === 'authors' ? (
                <div className="flex-1">
                  <CmsAuthorsView initial={params.get('author')} onEditing={setAuthorEditing} />
                </div>
              ) : view === 'forms' ? (
                <div className="flex-1">
                  <CmsFormsView
                    initial={params.get('form')}
                    fix={params.get('fix') === '1'}
                    openResponses={params.get('tab') === 'responses'}
                  />
                </div>
              ) : view === 'manage' && areaParam === 'dist' ? (
                // Distribution moved to Account → Connections (2026-10-24).
                <Navigate to="/settings/connections#feeds" replace />
              ) : view === 'manage' && movedManageArea(areaParam) ? (
                <Navigate to={movedManageArea(areaParam, window.location.hash)!} replace />
              ) : view === 'manage' ? (
                <div className="flex-1">
                  <ManageView area={manageArea} />
                </div>
              ) : view === 'analytics' ? (
                <div className="flex-1">
                  <CmsAnalytics />
                </div>
              ) : view === 'media' ? (
                <MediaLibrary
                  embedded
                  onReady={(open) => {
                    uploadMedia.current = open;
                  }}
                />
              ) : (
                <>
                  {accountId ? (
                    <FeedBringInBanner
                      accountId={accountId}
                      askOpen={bringAsk}
                      onAskHandled={clearBringAsk}
                      onShow={showFromBanner}
                      showing={source === 'waiting'}
                      onShowAll={() => setSource('all')}
                      onProgress={refetchList}
                    />
                  ) : null}
                  {/* No articles at all: no zeroed chips or "0 articles" over the
                      empty state (backlog d0a032ef). */}
                  {noArticles ? null : (
                    <div className="ink-lib-filters">
                      <input
                        className="ink-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search titles and summaries"
                        aria-label="Search titles and summaries"
                      />
                      {FILTERS.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          className="ink-chip"
                          data-active={filter === f.id ? 'true' : 'false'}
                          onClick={() => setFilter(f.id)}
                        >
                          {f.label} <span>{shownCount(counts?.[f.id])}</span>
                        </button>
                      ))}
                      {topicGroups.length > 0 && (
                        <>
                          <span className="ink-lib-rule" />
                          <TopicFilter
                            groups={topicGroups}
                            allCount={counts?.all ?? null}
                            value={topic?.key ?? null}
                            onChange={setTopic}
                          />
                        </>
                      )}
                      {hasFeeds || source !== 'all' ? (
                        <SourceFilter
                          sources={sources}
                          value={source}
                          onChange={(v) => {
                            setJobList(null);
                            setSource(v);
                          }}
                        />
                      ) : null}
                      {jobList ? (
                        <button
                          type="button"
                          className="ink-chip"
                          data-active="true"
                          title="Show every article again"
                          onClick={() => setJobList(null)}
                        >
                          {jobList.label} <span aria-hidden="true">✕</span>
                        </button>
                      ) : null}
                      <span className="ink-lib-count">
                        {page
                          ? `${total.toLocaleString()} article${total === 1 ? '' : 's'} · newest first`
                          : ''}
                      </span>
                    </div>
                  )}

                  <div className="flex-1">
                    {!page && !topicsError && (loading || !accountId) ? (
                      // The list's own frame in grey bars, also before the site is
                      // known (the query hasn't run), so "No articles match" never
                      // flashes first (QA round 7).
                      <ArticlesSkeleton facts={atomizeHeld} />
                    ) : topicsError && topics.length === 0 ? (
                      // A failed load is not an empty library: say so, or the
                      // person thinks their articles are gone.
                      <div className="ink-muted" style={{ padding: '48px 12px' }}>
                        <p>Your articles didn’t load. {topicsError.message}</p>
                        <button
                          type="button"
                          className="ink-btn"
                          style={{ marginTop: 12 }}
                          onClick={() => void refetchTopics()}
                        >
                          Try again
                        </button>
                      </div>
                    ) : items.length === 0 && noArticles ? (
                      // No articles at all (designer's notes, 2026-10-25): one
                      // sentence and New article ▾, as in the header.
                      <div className="rc-empty" style={{ marginTop: 20 }}>
                        <div className="rc-empty-title">No articles yet</div>
                        <p>
                          Ask your agent for a first draft, or write one yourself. Nothing goes live
                          until you approve it.
                        </p>
                        <div className="rcp-actions">
                          <AgentFirstButton
                            action="article"
                            label="New article"
                            align="left"
                            onSelf={() => navigate('/content/new')}
                          />
                        </div>
                      </div>
                    ) : items.length === 0 ? (
                      <p className="ink-muted" style={{ padding: '48px 12px' }}>
                        No articles match. Try a different filter or search.
                      </p>
                    ) : (
                      <div className={atomizeHeld ? undefined : 'ink-lib-nofacts'}>
                        <div className="ink-lib-head">
                          <span>Title</span>
                          <span>Topic</span>
                          <span>Author</span>
                          <span data-col="status">Status</span>
                          {atomizeHeld && <span>Facts</span>}
                          <span>Updated</span>
                        </div>
                        {rows.map((row) => {
                          if (row.kind === 'day') {
                            return (
                              <div key={row.key} className="ink-lib-day">
                                {row.label}
                              </div>
                            );
                          }
                          if (row.kind === 'batch') {
                            const open = Boolean(openBatches[row.key]) || Boolean(q);
                            return (
                              <div
                                key={row.key}
                                className="ink-lib-batch"
                                onClick={() => toggleBatch(row.key)}
                              >
                                <span className="ink-lib-chev">{open ? '▾' : '▸'}</span>
                                <span className="ink-lib-mark">{row.mark}</span>
                                <span>{row.label}</span>
                                {row.sub ? <span className="ink-lib-sub">{row.sub}</span> : null}
                                <span className="ink-lib-time">{row.time}</span>
                              </div>
                            );
                          }
                          if (row.kind === 'more') {
                            return (
                              <div
                                key={row.key}
                                className="ink-lib-more"
                                onClick={() => toggleBatch(row.batch)}
                              >
                                {row.label}
                              </div>
                            );
                          }
                          const topic = row.topic;
                          const bucket = libraryFilter(topic);
                          const tone = STATUS_STYLE[bucket];
                          const facts = factLabel(topic);
                          const summary = plainText(topic.summary);
                          return (
                            <div
                              key={topic.id}
                              className="ink-lib-row"
                              onClick={() => navigate(`/content/${topic.id}`)}
                            >
                              <div
                                className="ink-lib-main"
                                style={{ paddingLeft: row.indent ? 22 : 0 }}
                              >
                                <div className="ink-lib-title">
                                  {decode(topic.title || 'Untitled')}
                                  {/* Not in TinyCMS yet (Feed Articles design D): named by
                                      where it came from, quietly. */}
                                  {topic.fromFeed ? (
                                    <span className="fb-tag" title={topic.dataSource?.name}>
                                      From feed
                                    </span>
                                  ) : null}
                                </div>
                                {summary ? <div className="ink-lib-summary">{summary}</div> : null}
                                {emptyLive(topic) ? (
                                  <div className="ink-lib-dup">● {emptyLive(topic)}</div>
                                ) : null}
                                {/* A Bring all in's flagged article says what was found
                                    (backlog 2e9e8d9b), as a duplicate says so. */}
                                {topic.bringInFinding ? (
                                  <div className="ink-lib-dup">● {topic.bringInFinding}</div>
                                ) : null}
                                {topic.duplicateCount ? (
                                  <div className="ink-lib-dup">
                                    ● Possible duplicate · same title and body as{' '}
                                    {topic.duplicateCount} other live{' '}
                                    {topic.duplicateCount === 1 ? 'article' : 'articles'}
                                  </div>
                                ) : null}
                              </div>
                              <span className="ink-lib-topic">{topicLabel(topic.category)}</span>
                              {/* The byline (UX review round 12); unknown from an older server. */}
                              {topic.byline ? (
                                <span
                                  className="ink-lib-author"
                                  data-none={topic.byline.length ? undefined : 'true'}
                                  title={topic.byline.join(', ') || undefined}
                                >
                                  {bylineLabel(topic.byline)}
                                </span>
                              ) : (
                                <span className="ink-lib-author" />
                              )}
                              <span className="ink-lib-status" style={{ color: tone.text }}>
                                <span className="ink-dot" style={{ background: tone.dot }} />
                                {statusLabel(topic)}
                              </span>
                              {atomizeHeld && (
                                <span
                                  className="ink-lib-facts"
                                  style={{ color: facts ? 'var(--ink-ink2)' : 'var(--ink-line3)' }}
                                >
                                  {facts || '—'}
                                </span>
                              )}
                              <span className="ink-lib-time">{rowTime(topic)}</span>
                            </div>
                          );
                        })}
                        {total > PAGE_SIZE ? (
                          <div className="ink-lib-pager">
                            <span className="ink-muted">
                              {(offset + 1).toLocaleString()}–
                              {Math.min(offset + PAGE_SIZE, total).toLocaleString()} of{' '}
                              {total.toLocaleString()}
                            </span>
                            <button
                              type="button"
                              className="ink-btn"
                              disabled={offset === 0 || loading}
                              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                            >
                              Previous
                            </button>
                            <button
                              type="button"
                              className="ink-btn"
                              disabled={offset + PAGE_SIZE >= total || loading}
                              onClick={() => setOffset(offset + PAGE_SIZE)}
                            >
                              Next
                            </button>
                          </div>
                        ) : null}
                        {offset + PAGE_SIZE >= total ? (
                          <div className="ink-list-end">
                            <div className="ink-list-end-row">
                              <span className="ink-list-end-rule" />
                              <span className="ink-list-end-gem" />
                              <span className="ink-list-end-rule" />
                            </div>
                            <p className="ink-list-end-label">The end.</p>
                            <div className="ink-list-end-note">No more articles</div>
                          </div>
                        ) : null}
                      </div>
                    )}
                    {accountId ? (
                      <RecentlyDeleted
                        variant="list"
                        accountId={accountId}
                        items={deletedArticles}
                        restoreMutation={RESTORE_TINYCMS_ARTICLE}
                        onRestored={(id) => {
                          // Deleting dropped it from the app's cache; forget that so
                          // its page loads fresh instead of waiting on a gone entry.
                          apollo.cache.evict({
                            id: 'ROOT_QUERY',
                            fieldName: 'topic',
                            args: { id },
                          });
                          apollo.cache.gc();
                          void refetchTopics();
                          void refetchDeleted();
                          navigate(`/content/${id}`);
                        }}
                        note="Restored articles come back as drafts. Publish one again to put it back on your site."
                      />
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The Articles list (Prototype v7) in grey bars while its first page loads. */
function ArticlesSkeleton({ facts }: { facts: boolean }) {
  const widths = [72, 58, 66, 48, 70, 54, 62, 44];
  return (
    <div
      className={facts ? undefined : 'ink-lib-nofacts'}
      role="status"
      aria-label="Loading articles"
      aria-busy="true"
    >
      <div className="ink-lib-head" aria-hidden="true">
        <span>Title</span>
        <span>Topic</span>
        <span>Author</span>
        <span data-col="status">Status</span>
        {facts && <span>Facts</span>}
        <span>Updated</span>
      </div>
      {widths.map((w, i) => (
        <div key={i} className="ink-lib-row" aria-hidden="true" style={{ cursor: 'default' }}>
          <div className="ink-lib-main" style={{ display: 'grid', gap: 8 }}>
            <span className="ink-skel-bar" data-size="lg" style={{ width: `${w}%` }} />
            <span className="ink-skel-bar" style={{ width: `${w + 14}%` }} />
          </div>
          {/* The real cells' classes, so narrow screens hide the same columns. */}
          <span className="ink-skel-bar ink-lib-topic" style={{ width: 64 }} />
          <span className="ink-skel-bar ink-lib-author" style={{ width: 72 }} />
          <span className="ink-skel-bar ink-lib-status" style={{ width: 56 }} />
          {facts && <span className="ink-skel-bar ink-lib-facts" style={{ width: 28 }} />}
          <span className="ink-skel-bar ink-lib-time" style={{ width: 48 }} />
        </div>
      ))}
    </div>
  );
}
